import { useEffect, useRef, useState } from "react";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";

import { Select } from "antd";
import classNames from "classnames";
import { RootState } from "store/rootReducer";
import styled from "styled-components";

import { USER_DATA_SOURCES_QUERY_KEY, fetchUserDataSources } from "api/dataSource";

import { EntityKind } from "models/entityKind";
import { PdenSourceEnum } from "models/pdenDataSourceSetting";

import { useGroupByStore } from "./GroupByStoreProvider";
import {
  selectEntityKind,
  selectProps,
  selectSelectedPdenSource
} from "./groupBySelectors";

/* eslint-disable @typescript-eslint/no-explicit-any */
interface PdenSourceSelectInput {
  showLabel?: boolean;
  showGroup?: boolean;
  isDataTable?: boolean;
  initialState?: { selectedProperties: string[]; pdenSource: PdenSourceEnum };
  isFocused?: boolean;
  onSelectRef?: (index: number, selectEl: Maybe<HTMLElement>) => void;
  componentIndex?: number;
}
export default function PdenSourceSelect({
  showLabel = true,
  showGroup = false,
  isDataTable = false,
  initialState = null,
  isFocused = false,
  onSelectRef,
  componentIndex = 0
}: PdenSourceSelectInput) {
  const selectRef = useRef<any>(null);
  const selectedPdenSource = useGroupByStore(selectSelectedPdenSource);
  const entityKind = useGroupByStore(selectEntityKind);
  const props = useGroupByStore(selectProps);
  const initialStateSetRef = useRef(false);
  const setSelectedPdenSource = useGroupByStore((s) => s.setSelectedPdenSource);
  const groupBy = useSelector((state: RootState) => state.groupBy.globalGroupBy);
  const chartTypeParamSource = props?.chartTypeParam?.pdenSource?.toString();
  const pinnedPdenSource = props?.pinnedPdenSource;

  // enabled: false prevents React Query from auto-fetching on mount. This component exposes
  // refetch() for manual triggering. Because this query shares USER_DATA_SOURCES_QUERY_KEY
  // with useForecastPreferenceQuery (which is enabled by default), the cache slot may already
  // be populated by another active observer, in which case `data` will be available immediately
  // without an explicit refetch() call.
  const { data, refetch } = useQuery(USER_DATA_SOURCES_QUERY_KEY, fetchUserDataSources, {
    enabled: false,
    select: (response) => {
      if (response.ok) {
        const production = response.value.filter((ds) => ds.category === "Production");
        if (production.length == 1) {
          return production[0];
        }
      }
      return null;
    }
  });
  const [sourceOptions, setSourceOptions] = useState([]);
  const pdenSource = useSelector((state: RootState) => state.app.pdenDataSourceSetting);
  useEffect(() => {
    refetch();
  }, [pdenSource, refetch, chartTypeParamSource]);

  useEffect(() => {
    if (isDataTable) {
      return;
    }
    if (!sourceOptions || sourceOptions.length === 0) {
      return;
    }
    // A caller that opens this picker on something holding a source of its own names it
    // here, and it outranks the committed focus below: a focus tile in user settings keeps
    // its own source, while the focus describes whatever the tab is showing.
    //
    // Compared against null rather than tested for truth, because gDC is 0.
    if (
      pinnedPdenSource != null &&
      sourceOptions.find((opt) => opt.value === pinnedPdenSource)
    ) {
      setSelectedPdenSource(pinnedPdenSource);
      return;
    }
    if (
      entityKind === EntityKind.Well &&
      (groupBy.property.toLowerCase().includes("performance") ||
        groupBy.property.toLowerCase().startsWith("turingpadmetrics")) &&
      sourceOptions.find((opt) => opt.value === parseInt(groupBy.pdenSource))
    ) {
      setSelectedPdenSource(parseInt(groupBy.pdenSource));
      return;
    }
    setSelectedPdenSource(sourceOptions[0].value);
  }, [sourceOptions, groupBy, entityKind, isDataTable, pinnedPdenSource]);

  useEffect(() => {
    if (!isDataTable) {
      return;
    }
    if (!sourceOptions || sourceOptions.length === 0) {
      return;
    }

    const hasInitialPdenSource = initialState?.pdenSource != null;
    const isFirstRender = !initialStateSetRef.current;
    const isValidSource = sourceOptions.find(
      (opt) => opt.value === initialState?.pdenSource
    );

    if (hasInitialPdenSource && isFirstRender && isValidSource) {
      setSelectedPdenSource(initialState.pdenSource);
      initialStateSetRef.current = true;
    } else {
      setSelectedPdenSource(sourceOptions[0].value);
    }
  }, [sourceOptions, entityKind, isDataTable, initialState]);

  useEffect(() => {
    if (!data) {
      setSourceOptions([]);
      return;
    }

    if (
      chartTypeParamSource
        ? chartTypeParamSource === PdenSourceEnum.Public.toString()
        : pdenSource.source == "Public"
    ) {
      setSourceOptions(
        [{ value: PdenSourceEnum.Public, label: "All Public Sources" }].concat(
          data.dataSources.map((ds) => ({
            value: parseInt(ds.source),
            label: ds.dataSourceDisplayName ?? ds.dataSourceName
          }))
        )
      );
    } else if (
      chartTypeParamSource
        ? chartTypeParamSource === PdenSourceEnum.Private.toString()
        : pdenSource.source == "Private"
    ) {
      setSourceOptions([{ value: PdenSourceEnum.Private, label: "Private Source" }]);
    } else {
      setSourceOptions(
        [
          {
            value: PdenSourceEnum.Hybrid,
            label: "Hybrid - Private trumps Public"
          },
          { value: PdenSourceEnum.Private, label: "Private" }
        ].concat(
          data.dataSources.map((ds) => ({
            value: parseInt(ds.source),
            label: ds.dataSourceDisplayName ?? ds.dataSourceName
          }))
        )
      );
    }
  }, [data, pdenSource, chartTypeParamSource]);

  if (!data) {
    return <Select />;
  }

  return (
    <SelectWrapper>
      {showLabel && <label>Production Source</label>}
      <Select
        ref={(el) => {
          selectRef.current = el;
          if (onSelectRef) onSelectRef(componentIndex, el as HTMLElement);
        }}
        className={classNames("toolbar-item", {
          "keyboard-focused": isFocused
        })}
        value={selectedPdenSource}
        onChange={(val) => {
          setSelectedPdenSource(val);
          // Blur the select after selection
          if (selectRef.current) {
            selectRef.current.blur();
          }
        }}
        options={
          showGroup ? [{ label: "Pden Source", options: sourceOptions }] : sourceOptions
        }></Select>
    </SelectWrapper>
  );
}

const SelectWrapper = styled.div`
  min-width: 100px;
  width: 100%;
  .ant-select {
    width: 100%;
  }
`;
